"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { getBrandConfig, type BrandKeyT } from "@/app/config/brand-registry";

type BrandThemeBoundaryProps = {
  brandKey: BrandKeyT;
  className?: string;
  children: React.ReactNode;
};

type BrandContextValueT = {
  brandKey: BrandKeyT;
  isDefaultBrand: boolean;
  isSkoda: boolean;
  isAudi: boolean;
};

const BrandContext = React.createContext<BrandContextValueT | null>(null);

export function BrandThemeBoundary({
  brandKey,
  className,
  children,
}: BrandThemeBoundaryProps) {
  const brandConfig = getBrandConfig(brandKey);
  const contextValue = React.useMemo<BrandContextValueT>(
    () => ({
      brandKey,
      isDefaultBrand: brandKey === "default",
      isSkoda: brandKey === "skoda",
      isAudi: brandKey === "audi",
    }),
    [brandKey]
  );

  // Sync data-brand to <html> so Radix portals (SelectContent, etc.) inherit CSS vars.
  // Only needed for Audi — Skoda's green link-color would otherwise leak into the navbar.
  React.useEffect(() => {
    const root = document.documentElement;
    if (brandKey === "audi") {
      root.setAttribute("data-brand", brandKey);
    }
    return () => {
      root.removeAttribute("data-brand");
    };
  }, [brandKey]);

  return (
    <BrandContext.Provider value={contextValue}>
      <div {...brandConfig.themeAttributes} className={cn(className)}>
        {children}
      </div>
    </BrandContext.Provider>
  );
}

export function useBrand(): BrandContextValueT {
  const context = React.useContext(BrandContext);

  if (!context) {
    return {
      brandKey: "default",
      isDefaultBrand: true,
      isSkoda: false,
      isAudi: false,
    };
  }

  return context;
}
